import { useLingui } from '@lingui/react/macro';

import { buildShiftColumns, computeVariantByShiftId } from '@/domain/templates/preview-columns';
import { buildShiftStacks } from '@/domain/templates/preview-stacks';
import {
  ANY_STAFF_GROUP_ID,
  SPECIFIC_PEOPLE_ID,
  groupShiftsByStaffGroup,
  type StaffGroupBucketView,
} from '@/domain/templates/staff-group-preview-data';
import { fixedDayCount, type DayCountResolver } from '@/domain/templates/staffing';
import type { ShiftSet } from '@/types/template';

import { PreviewGrid } from './preview-grid';
import { StackedGrid } from './stacked-grid';

interface StaffGroupPreviewProps {
  /** Saved shift patterns to render, grouped by restriction staff group. */
  shifts: ShiftSet[];
  /** The pattern currently being edited, rendered as a draft (ghost) pill. */
  draftShift?: ShiftSet | null;
  /** Collapse same type+time shifts into `×N` decks rather than per-head pills. */
  stacked?: boolean;
  /** Per-day head-count resolver (defaults to the fixed staffing count). */
  resolveCount?: DayCountResolver;
}

/**
 * The "Staff group" view of a template preview: a Mon–Sun grid per staff group,
 * grouped by each shift's restriction groups (`groupShiftsByStaffGroup`). A
 * shift restricted to several groups appears under each. When `stacked`, each
 * bucket renders `×N` decks ({@link StackedGrid}); otherwise one pill per head
 * ({@link PreviewGrid}), with shading variants computed across all shifts so
 * they match the shift-type view.
 */
export function StaffGroupPreview({
  shifts,
  draftShift = null,
  stacked = true,
  resolveCount = fixedDayCount,
}: StaffGroupPreviewProps) {
  const { t } = useLingui();

  // Include the draft in the grouping so its section appears (and is ordered)
  // even when no saved shift shares that bucket; it's rendered as a ghost below.
  const buckets = groupShiftsByStaffGroup(draftShift ? [...shifts, draftShift] : shifts);
  const variantByShiftId = computeVariantByShiftId(shifts);

  function label(bucket: StaffGroupBucketView): string {
    if (bucket.id === ANY_STAFF_GROUP_ID) return t`Any staff group`;
    if (bucket.id === SPECIFIC_PEOPLE_ID) return t`Specific people`;
    return bucket.name;
  }

  return (
    <div data-testid="staff-group-preview" className="flex flex-col gap-3">
      {buckets.map((bucket) => {
        const saved = draftShift ? bucket.shifts.filter((s) => s !== draftShift) : bucket.shifts;
        const draftHere = draftShift && bucket.shifts.includes(draftShift) ? draftShift : null;
        return (
          <section key={bucket.id} data-testid={`staff-group-${bucket.id}`} className="space-y-1.5">
            <h4 className="text-sm font-semibold text-foreground">{label(bucket)}</h4>
            {stacked ? (
              <StackedGrid columns={buildShiftStacks(saved, draftHere, resolveCount)} />
            ) : (
              <PreviewGrid
                columns={buildShiftColumns(saved, draftHere, variantByShiftId, resolveCount)}
              />
            )}
          </section>
        );
      })}
    </div>
  );
}
